import React from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import './AluminumTrailersPage.css'
import { getAluminumSingleAxleBunkModels, getAluminumTandemAxleBunkModels, getAluminumTripleAxleBunkModels, getAluminumSingleAxleRollerModels, getAluminumTandemAxleRollerModels } from '../data/trailerModels'

const AluminumTrailersPage: React.FC = () => {
  // Get real aluminum models from CSV data organized by type and axle configuration
  const singleAxleBunkModels = getAluminumSingleAxleBunkModels();
  const tandemAxleBunkModels = getAluminumTandemAxleBunkModels();
  const tripleAxleBunkModels = getAluminumTripleAxleBunkModels();
  const singleAxleRollerModels = getAluminumSingleAxleRollerModels();
  const tandemAxleRollerModels = getAluminumTandemAxleRollerModels();

  const aluminumTrailerFeatures = [
    "Lightweight aluminum construction for maximum fuel economy",
    "Superior corrosion resistance for marine environments", 
    "Plug & Play LED lighting system",
    "Aluminum diamond plate fenders",
    "Heavy duty winch straps and hardware",
    "D.O.T. composite brake lines with brass fittings",
    "Balanced radial tires with professional balancing",
    "Eliminator GalvX vented rotor disc brakes",
    "Super lube spindles for smooth operation",
    "Galvanized hardware and marine-grade fasteners",
    "Carpeted bunks for hull protection (bunk models)",
    "Articulating roller system (roller models)"
  ];

  return (
    <div className="aluminum-page">
      <SEOHead
        title="Aluminum Boat Trailers - Lightweight & Corrosion Resistant | Fast Boat Trailers"
        description="Premium aluminum boat trailers from Fast Boat Trailers. Lightweight, corrosion-resistant aluminum construction for superior fuel economy and marine durability. Bunk & roller options available."
        keywords="aluminum boat trailers, lightweight trailers, corrosion resistant trailers, aluminum bunk trailers, aluminum roller trailers, marine trailers, saltwater trailers"
        ogTitle="Aluminum Boat Trailers - Lightweight & Corrosion Resistant"
        ogDescription="Premium aluminum boat trailers from Fast Boat Trailers. Lightweight, corrosion-resistant construction for superior marine performance."
        ogImage="/images/trailers/aluminum-single-axle-bunk.jpg"
        canonical="https://fastboattrailers.com/trailers/aluminum"
      />
      <div className="container">
        {/* Hero Section */}
        <div className="aluminum-hero">
          <div className="hero-main">
            <div className="hero-content">
              <h1>Aluminum Trailers</h1>
              <h2>Lightweight Performance & Corrosion Resistance</h2>
              <p>
                Venture Aluminum Trailers combine lightweight construction with superior durability. 
                Perfect for marine environments, these trailers offer excellent fuel economy, 
                corrosion resistance, and professional-grade features that protect your boat investment.
              </p>
              
              <h3>Benefits of Aluminum Trailers</h3>
              <ul className="benefits-list">
                <li>Lightweight design maximizes towing capacity</li>
                <li>Superior corrosion resistance for saltwater use</li>
                <li>Better fuel economy than steel alternatives</li>
                <li>Modern appearance that maintains value over time</li>
              </ul>
            </div>
            <div className="hero-features">
              <h3>Standard Features</h3>
              <div className="features-compact">
                {aluminumTrailerFeatures.map((feature, index) => (
                  <div key={index} className="feature-item-compact">
                    <div className="feature-icon-compact">✓</div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Aluminum Bunk Trailers Section */}
        <div className="models-section">
          <h2>Aluminum Bunk Trailers</h2>
          
          {/* Single Axle Bunk */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/aluminum-single-axle-bunk.jpg" alt="Venture aluminum single axle bunk boat trailers - lightweight marine grade construction 1000-3500 lbs capacity Maryland" />
            </div>
            <div className="category-header">
              <h3>Aluminum Single Axle Bunk Trailers</h3>
              <p>Lightweight single axle design perfect for smaller boats. Carpeted bunks provide gentle hull support with capacity ranges from {singleAxleBunkModels[0]?.capacity.toLocaleString()} to {singleAxleBunkModels[singleAxleBunkModels.length - 1]?.capacity.toLocaleString()} lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Brakes</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                  </tr>
                </thead>
                <tbody>
                  {singleAxleBunkModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tandem Axle Bunk */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/aluminum-tandem-axle-bunk.jpg" alt="Venture aluminum tandem axle bunk boat trailers - corrosion resistant saltwater rated 3500-9000 lbs capacity Edgewater MD" />
            </div>
            <div className="category-header">
              <h3>Aluminum Tandem Axle Bunk Trailers</h3>
              <p>Enhanced stability and load distribution for mid to large-size boats. Superior aluminum construction with capacity ranges from {tandemAxleBunkModels[0]?.capacity.toLocaleString()} to {tandemAxleBunkModels[tandemAxleBunkModels.length - 1]?.capacity.toLocaleString()} lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Brakes</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                  </tr>
                </thead>
                <tbody>
                  {tandemAxleBunkModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Triple Axle Bunk */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/aluminum-triple-axle-bunk.jpg" alt="Venture aluminum triple axle bunk boat trailers - heavy duty marine transport 9500-16000 lbs capacity large boats Maryland" />
            </div>
            <div className="category-header">
              <h3>Aluminum Triple Axle Bunk Trailers</h3>
              <p>Maximum capacity aluminum design for the largest boats. Ultimate load distribution and stability with capacity ranges from {tripleAxleBunkModels[0]?.capacity.toLocaleString()} to {tripleAxleBunkModels[tripleAxleBunkModels.length - 1]?.capacity.toLocaleString()} lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Brakes</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                  </tr>
                </thead>
                <tbody>
                  {tripleAxleBunkModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Aluminum Roller Trailers Section */}
        <div className="models-section">
          <h2>Aluminum Roller Trailers</h2>
          
          {/* Single Axle Roller */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/roller-aluminum-single-axle.jpg" alt="Venture aluminum single axle roller boat trailers - easy launch shallow water 2400-3500 lbs capacity articulating rollers" />
            </div>
            <div className="category-header">
              <h3>Aluminum Single Axle Roller Trailers</h3>
              <p>Lightweight aluminum roller trailers perfect for shallow water launching. Articulating roller system with capacity ranges from {singleAxleRollerModels[0]?.capacity.toLocaleString()} to {singleAxleRollerModels[singleAxleRollerModels.length - 1]?.capacity.toLocaleString()} lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Brakes</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                  </tr>
                </thead>
                <tbody>
                  {singleAxleRollerModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tandem Axle Roller */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/roller-aluminum-tandem-axle.jpg" alt="Venture aluminum tandem axle roller boat trailers - professional grade marine transport 3800-11200 lbs capacity smooth launching system" />
            </div>
            <div className="category-header">
              <h3>Aluminum Tandem Axle Roller Trailers</h3>
              <p>Enhanced stability and load distribution with smooth roller launching. Aluminum construction with capacity ranges from {tandemAxleRollerModels[0]?.capacity.toLocaleString()} to {tandemAxleRollerModels[tandemAxleRollerModels.length - 1]?.capacity.toLocaleString()} lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Brakes</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                  </tr>
                </thead>
                <tbody>
                  {tandemAxleRollerModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="aluminum-cta">
          <h2>Ready to Get Your Aluminum Trailer?</h2>
          <p>
            Find the perfect aluminum trailer for your boat with superior corrosion resistance, 
            lightweight performance, and professional-grade features that protect your investment.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">Get a quote</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AluminumTrailersPage;